import {
	ClockCircleOutlined,
	ExclamationCircleOutlined
} from '@ant-design/icons';
import { Table, Tooltip } from 'antd';
import { DateDisplay } from 'components/Global/DateDisplay';
import DownloadComponent from 'components/Global/Download';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatDate, invalidText, minutesToTimeString } from 'utils/utils';
import styles from './index.module.scss';

const { Column } = Table;

type Props = {
	tableData: any;
	totalRecords: number;
	currentPage: number;
	pageSize: number;
	tableChangeHandler: (pagination: any, filter: any, sorter: any) => void;
	isLoading: boolean;
	statusFilterValue: any;
};

const TimeLogsTable = (props: Props) => {
	const {
		tableData,
		totalRecords,
		currentPage,
		pageSize,
		tableChangeHandler,
		isLoading,
		statusFilterValue,
	} = props;

	const navigate = useNavigate();
	const { data: profileData } = useSelector(
		(state: any) => state?.userProfile
	);

	// Helper function to check if row has data
	const hasData = (record: any) => {
		return (
			record.totalHours > 0 ||
			record.present > 0 ||
			record.absent > 0 ||
			record.submittedBy
		);
	};

	const handleTotalHoursClick = (data: any) => {
		if (hasData(data)) {
			// Navigate to time-logs with supervisor and date params
			const url = `/time-logs?isDateDisable=true&supervisor=${
				data.supervisorId
			}&date=${dayjs(data.date).format('YYYY-MM-DD')}`;
			navigate(url, {
				state: { from: '/daily-attendance' },
			});
		} else {
			// Same navigation as Add Time Logs for no data
			const url = `/time-logs?isDateDisable=true&date=${dayjs(
				data.date
			).format('YYYY-MM-DD')}${
				invalidText(statusFilterValue)
					? ''
					: `&supervisor=${statusFilterValue}`
			}`;
			navigate(url, {
				state: { from: '/daily-attendance' },
			});
		}
	};
	return (
		<div style={{ width: '100%', overflowX: 'auto' }}>
			<Table
				dataSource={tableData}
				pagination={{
					total: totalRecords,
					current: currentPage,
					pageSize: pageSize,
					showSizeChanger: false,
					pageSizeOptions: [10, 20, 50, 100, 200],
				}}
				scroll={
					tableData.length > 0
						? { x: 1700, y: 'calc(100vh - 360px)' } // Increased x for new column
						: undefined
				}
				onChange={tableChangeHandler}
				rowKey={(record) => record.id}
				loading={isLoading}
				rowClassName={(record) => {
					// Add visual distinction for empty rows
					return !hasData(record) ? 'empty-row' : '';
				}}
			>
				<Column
					title="Date"
					dataIndex="date"
					key="date"
					fixed="left"
					className="bg-white"
					width={'12%'}
					render={(value, record: any) => {
						const isSunday = dayjs(value).day() === 0;
						const isHoliday = record.isHoliday || false;

						let dateClass;
						if (isHoliday) {
							dateClass = styles['date-holiday'];
						} else if (isSunday) {
							dateClass = styles['date-sunday'];
						}

						const attendanceTooltip = isHoliday
							? record.holidayName
							: '';

						return (
							<Tooltip title={attendanceTooltip}>
								<div className={dateClass}>
									<DateDisplay
										date={value}
										dateFormat={
											profileData?.companyDateFormat ??
											'DD/MM/YYYY'
										}
										compact={true}
										isHoliday={isHoliday} // ← ADD THIS
										highlightSpecialDays={true}
									/>
								</div>
							</Tooltip>
						);
					}}
				/>

				<Column
					title="Total Hours"
					dataIndex="totalHours"
					key="totalHours"
					width="10%"
					// sorter={true}
					className="bg-white"
					render={(value, data: any) => {
						const recordHasData = hasData(data);

						if (!recordHasData) {
							return (
								<Tooltip title="Add Logs" placement="top">
									{' '}
									<ClockCircleOutlined
										style={{
											cursor: 'pointer',
											fontSize: '16px',
											textAlign: 'center',
											display: 'flex',
										}}
										onClick={() =>
											handleTotalHoursClick(data)
										}
									/>
								</Tooltip>
							);
						}
						return (
							<span
								className="cursor-pointer"
								style={{
									cursor: 'pointer',
									color: '#584495',
								}}
								onClick={() => handleTotalHoursClick(data)}
							>
								{value > 0
									? `${minutesToTimeString(value)} Hours`
									: '0 Hours'}
							</span>
						);
					}}
				/>
				<Column
					title={
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								cursor: 'pointer',
							}}
						>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '4px',
								}}
							>
								<p style={{ fontWeight: 600 }}>Attendance</p>
								<Tooltip
									title={
										<div
											className={
												styles['tooltip-content']
											}
										>
											<div>
												<strong>Present :</strong> No.
												of employees present on that day
											</div>
											<div>
												<strong>Absent :</strong> No. of
												employees absent on that day
											</div>
											<div>
												<strong>Pending :</strong> No.
												of employees whose logs are
												pending for that day
											</div>
										</div>
									}
									placement="right"
								>
									<ExclamationCircleOutlined
										style={{
											color: '#544895',
											cursor: 'pointer',
										}}
									/>
								</Tooltip>
							</div>

							<span
								style={{
									color: 'gray',
									fontWeight: 300,
									fontSize: '10px',
								}}
							>
								Present | Absent | Pending
							</span>
						</div>
					}
					dataIndex="attendance"
					key="attendance"
					width="12%"
					className="bg-white"
					render={(value, record: any) => {
						const present = record.present || 0;
						const absent = record.absent || 0;
						const pending = record.pendingEmployees || 0;

						return (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: 4,
								}}
							>
								<span
									style={{
										color: 'green',
										minWidth: 38,
										textAlign: 'center',
									}}
								>
									{present}
								</span>
								<span style={{ color: '#999' }}>|</span>
								<span
									style={{
										color: 'red',
										minWidth: 38,
										textAlign: 'center',
									}}
								>
									{absent}
								</span>
								<span style={{ color: '#999' }}>|</span>
								<span
									style={{
										color: '#ccc',
										minWidth: 38,
										textAlign: 'center',
									}}
								>
									{pending}
								</span>
							</div>
						);
					}}
				/>

				{statusFilterValue === '' && (
					<Column
						title="Supervisor Name"
						dataIndex="supervisorName"
						key="supervisorName"
						width="10%"
						className="bg-white"
						render={(value) => value || '-'}
					/>
				)}
				<Column
					title="Shift Name"
					dataIndex="shiftName"
					key="shiftName"
					width="10%"
					className="bg-white"
					render={(value) => value || '-'}
				/>
				<Column
					title="Submitted By"
					dataIndex="submittedBy"
					key="submittedBy"
					width="15%"
					className="bg-white"
					render={(value) => value || '-'}
				/>
				<Column
					title="Submitted On"
					dataIndex="submittedOn"
					key="submittedOn"
					// sorter={true}
					width="10%"
					className="bg-white"
					render={(value) => {
						return value
							? formatDate(
									value,
									profileData?.companyDateFormat ??
										'DD/MM/YYYY'
							  )
							: '-';
					}}
				/>
				<Column
					title="Attachment"
					dataIndex="attachment"
					key="attachment"
					width="15%"
					className="bg-white"
					render={(value, data: any) =>
						data.attachmentUrl && data.attachmentName ? (
							<DownloadComponent
								documentUrl={data.attachmentUrl}
								downloadText={data.attachmentName}
								showTooltip={false}
								text={data.attachmentName}
								isLink={true}
								maxLength={20}
							/>
						) : (
							'-'
						)
					}
				/>
			</Table>
		
		</div>
	);
};

export default TimeLogsTable;
